<template>
  <div>
    <dialog-loading v-if="saving"></dialog-loading>
    <v-dialog v-model="dialog" scrollable :max-width="is_from_order ? '720px' : '1100px'" persistent transition="dialog-bottom-transition">
      <v-card class="charge-dialog-card">
        <div class="charge-dialog-header charge-dialog-header--pix">
          <div class="d-flex align-center">
            <v-avatar color="white" size="40" class="mr-3 elevation-1">
              <v-icon color="teal darken-2">mdi-qrcode</v-icon>
            </v-avatar>
            <div>
              <div class="headline white--text mb-0">Novo PIX Cobrança</div>
              <div class="caption white--text" style="opacity:.85">
                Multa 2% · Juros 1% a.m. após o vencimento
              </div>
            </div>
          </div>
        </div>

        <v-card-text class="pa-5">
          <v-form ref="new_pix_cobranca_form" lazy-validation>
            <template v-if="is_from_order">
              <v-row dense class="mb-4">
                <v-col cols="12" sm="6">
                  <div class="charge-summary-card">
                    <div class="caption grey--text text--darken-1">Cliente</div>
                    <div class="subtitle-1 font-weight-medium text-truncate">{{ order_data.client_name }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="charge-summary-card">
                    <div class="caption grey--text text--darken-1">Total PIX Cobrança</div>
                    <div class="subtitle-1 font-weight-bold primary--text">
                      {{ formatMoney(order_config.ammount) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" v-if="paymentDescription">
                  <div class="charge-summary-card">
                    <div class="caption grey--text text--darken-1">Prazo</div>
                    <div class="body-2 font-weight-medium">{{ paymentDescription }}</div>
                  </div>
                </v-col>
              </v-row>
            </template>

            <template v-if="!is_from_order">
              <v-row dense class="mb-2">
                <v-col cols="12">
                  <SelectModality :object_with_model="form" custom_label="Módulo" outlined dense preselect_active />
                </v-col>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="form.client_id"
                    :items="allClients"
                    label="Cliente"
                    prepend-inner-icon="mdi-account"
                    :item-text="formatClientDisplayName"
                    item-value="id"
                    :rules="[rules.required]"
                    :loading="loading_clients"
                    outlined
                    dense
                    hide-details="auto"
                  >
                    <template v-slot:selection="{ item }">
                      {{ formatClientDisplayName(item) }}
                      <span v-if="item.attributes?.fantasyName || item.attributes?.fantasy_name">
                        ({{ item.attributes.fantasyName || item.attributes.fantasy_name }})
                      </span>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          Nome/Fantasia: <b>{{ item.attributes?.fantasyName }}</b>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          Razão Social: <b>{{ formatClientDisplayName(item) }}</b>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-menu v-model="standalone_due_menu" :close-on-content-click="false" offset-y min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="$moment(standalone_due).format('DD/MM/YYYY')"
                        :error-messages="dueErrors(standalone_due)"
                        label="Vencimento"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details="auto"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker locale="pt-br" v-model="standalone_due" :min="today_str" @input="standalone_due_menu = false"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </template>

            <v-select
              v-model="company_key"
              :items="sicoob_credential_options"
              item-text="name"
              item-value="key"
              label="Empresa / chave Sicoob"
              prepend-inner-icon="mdi-domain"
              :rules="[rules.required]"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            ></v-select>

            <template v-if="!is_from_order">
              <v-row dense class="mb-3">
                <v-col cols="12" sm="6">
                  <div class="charge-summary-card d-flex align-center">
                    <v-avatar size="40" color="green lighten-5" class="mr-3">
                      <v-icon color="green darken-2">mdi-cash-multiple</v-icon>
                    </v-avatar>
                    <div>
                      <div class="caption grey--text text--darken-1">Valor do PIX Cobrança</div>
                      <div v-if="vrts_is_selected" class="subtitle-1 font-weight-bold">{{ formatMoney(paying_vrts_total) }}</div>
                      <div v-else class="body-2 font-italic error--text">Selecione a fatura abaixo</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="charge-summary-card d-flex align-center">
                    <v-avatar size="40" color="red lighten-5" class="mr-3">
                      <v-icon color="red darken-2">mdi-book-open-variant-outline</v-icon>
                    </v-avatar>
                    <div>
                      <div class="caption grey--text text--darken-1">Em aberto ({{ modality_name }})</div>
                      <div class="subtitle-1 font-weight-bold">{{ formatMoney(selected_vrts_data.total_debt) }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <VrtSelection
                v-if="form.client_id"
                :client_id="form.client_id"
                manual_values
                single_select
                @onChange="handleChangeVrtSelection"
                class="mb-4"
              ></VrtSelection>
              <div v-else class="charge-empty-box text-center pa-6 mb-4">
                <v-icon size="40" color="blue-grey lighten-3">mdi-account-search-outline</v-icon>
                <div class="body-2 grey--text mt-2">Escolha um cliente para exibir as faturas.</div>
              </div>
            </template>

            <template v-if="is_from_order && pix_cobranca_aux.length">
              <div class="d-flex align-center mb-2">
                <div class="subtitle-2 font-weight-bold grey--text text--darken-2">Parcelas</div>
                <v-spacer></v-spacer>
                <v-chip x-small outlined>{{ pix_cobranca_aux.length }}</v-chip>
              </div>

              <v-card
                v-for="(c_aux, idx) in pix_cobranca_aux"
                :key="'c' + idx"
                outlined
                class="charge-parcel-card mb-3"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="28" color="teal lighten-5" class="mr-2">
                      <span class="teal--text text--darken-2 font-weight-bold">{{ parcelLetter(idx) }}</span>
                    </v-avatar>
                    <span class="subtitle-2 font-weight-medium">PIX Cobrança {{ parcelLetter(idx) }}</span>
                  </div>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="c_aux.value"
                        v-currency="currency"
                        :rules="[rules.required, rules.notZero]"
                        prepend-inner-icon="mdi-currency-brl"
                        label="Valor"
                        prefix="R$"
                        outlined
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-menu v-model="c_aux.menu" :close-on-content-click="false" offset-y min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="$moment(c_aux.due).format('DD/MM/YYYY')"
                            :error-messages="dueErrors(c_aux.due)"
                            label="Vencimento"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            outlined
                            dense
                            hide-details="auto"
                            v-bind="attrs"
                            v-on="on"
                            :rules="[rules.required]"
                          ></v-text-field>
                        </template>
                        <v-date-picker locale="pt-br" v-model="c_aux.due" :min="today_str" @input="c_aux.menu = false"></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </template>

            <div
              v-if="is_from_order"
              class="charge-sum-box mt-2"
              :class="sum_same_as_order ? 'charge-sum-box--ok' : 'charge-sum-box--err'"
            >
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="caption font-weight-medium">Soma das parcelas</span>
                <span class="subtitle-2 font-weight-bold">
                  {{ formatMoney(sum_pix_cobrancas) }}
                  <span class="caption font-weight-regular"> / {{ formatMoney(order_config.ammount) }}</span>
                </span>
              </div>
              <v-progress-linear
                :value="sumProgress"
                height="6"
                rounded
                :color="sum_same_as_order ? 'success' : 'warning'"
              ></v-progress-linear>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <f-btn cancel @click="dialog = false"></f-btn>
          <f-btn
            save
            alt_text="Gerar PIX Cobrança"
            alt_icon="mdi-check"
            @click="submitPixCobrancas"
            :disabled="!can_submit"
            :loading="saving"
          ></f-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { CurrencyDirective } from 'vue-currency-input'
import Api from '@/api/index'
import sicoob_credentials from '@/_backoffice/_financial/mixins/sicoob_credentials'
import clientDisplay from '@/mixins/clientDisplay'

export default {
  name: 'DialogNewPixCobranca',
  directives: { currency: CurrencyDirective },
  mixins: [sicoob_credentials, clientDisplay],
  components: {
    SelectModality: () => import('@/components/shared/SelectModality.vue'),
    VrtSelection: () => import('@/_backoffice/_financial/components/order_payment/VrtSelection.vue'),
  },
  props: {
    is_from_order: { type: Boolean, default: false },
    order_data: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      dialog: true,
      saving: false,
      company_key: '',
      pix_cobranca_aux: [],
      standalone_due: this.$moment().add(7, 'day').format('YYYY-MM-DD'),
      standalone_due_menu: false,
      allClients: [],
      loading_clients: false,
      form: {
        client_id: null,
        place_id: null,
      },
      selected_vrts_data: {
        paying_vrts: [],
        selected_vrts: [],
        selected_vrts_ammount: '0.00',
        total_debt: '0.00',
      },
      currency: {
        currency: null,
        locale: 'pt-BR',
        autoDecimalMode: true,
        decimalLength: 2,
        distractionFree: false,
        valueAsInteger: false,
      },
      rules: {
        required: (value) => !!value || 'Campo é necessário',
        notZero: (value) => {
          const raw = String(value || '0')
          const amount = raw.includes(',')
            ? Number(raw.replace(/\./g, '').replace(',', '.'))
            : Number(raw.replace(/,/g, ''))
          return (amount > 0) || 'Não pode ser zero'
        },
      },
    }
  },
  computed: {
    paymentDescription() {
      const description = this.order_config.description
      if (!description) return ''
      if (typeof description === 'string') return description
      return description.description || description.attributes?.description || ''
    },
    sumProgress() {
      const target = Number(this.order_config.ammount) || 0
      if (target <= 0) return 0
      return Math.min(100, (this.sum_pix_cobrancas / target) * 100)
    },
    order_config() {
      if (!this.is_from_order) {
        return { times: 1, offset: 0, daysBase: 0, ammount: 0, description: 'PIX Cobrança' }
      }
      const pix_cobranca_payment = (this.order_data.list || []).find(
        ({ payment_option }) => (payment_option || '').toLowerCase() === 'pix cobrança'
      )
      if (!pix_cobranca_payment) return { times: 1, offset: 0, daysBase: 0, ammount: 0, description: 'PIX Cobrança' }
      const time_config = pix_cobranca_payment.time_config || {}
      const { offset, daysBase, timesBase } = time_config
      const order_payment_values = (this.order_data.pay_times?.list || []).find(
        ({ payment_option_id }) => payment_option_id == pix_cobranca_payment.payment_option_id
      )
      return {
        ...(order_payment_values || {}),
        times: Number(timesBase) || 1,
        offset: Number(offset) || 0,
        daysBase: Number(daysBase) || 0,
        description: pix_cobranca_payment.payment_time,
      }
    },
    sum_pix_cobrancas() {
      return Number(
        this.pix_cobranca_aux.reduce((all, one) => this.parseMoney(one.value) + all, 0).toFixed(2)
      )
    },
    sum_same_as_order() {
      return this.sum_pix_cobrancas.toFixed(2) === Number(this.order_config.ammount || 0).toFixed(2)
    },
    today_str() {
      return this.$moment().format('YYYY-MM-DD')
    },
    parcels_ok() {
      return this.pix_cobranca_aux.length > 0
        && this.pix_cobranca_aux.every((parcel) => this.parseMoney(parcel.value) > 0 && !!parcel.due && parcel.due >= this.today_str)
    },
    vrts_is_selected() {
      return this.selected_vrts_data.selected_vrts.length > 0
    },
    paying_vrts_total() {
      return Number(this.selected_vrts_data.selected_vrts_ammount) || 0
    },
    standalone_due_ok() {
      return !!this.standalone_due && this.standalone_due >= this.today_str
    },
    can_submit() {
      if (this.saving || !this.company_key) return false
      if (this.is_from_order) return this.parcels_ok && this.sum_same_as_order
      return !!(this.form.client_id && this.form.place_id)
        && this.vrts_is_selected
        && this.paying_vrts_total > 0
        && this.standalone_due_ok
    },
    ...mapState({
      modality_name: (state) => state.App.activeModalityName,
    }),
  },
  methods: {
    dueErrors(due) {
      return due && due < this.today_str ? ['Vencimento não pode ser no passado'] : []
    },
    parseMoney(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0
      const raw = String(value ?? '').trim()
      if (!raw) return 0
      const hasComma = raw.includes(',')
      const hasDot = raw.includes('.')
      if (hasComma && hasDot) {
        if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
          return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
        }
        return Number(raw.replace(/,/g, '')) || 0
      }
      if (hasComma) return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
      return Number(raw) || 0
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    parcelLetter(idx) {
      return String.fromCharCode(65 + idx)
    },
    baseDate() {
      const loadDate = this.order_data.loaded_at || this.order_data.expected_delivery_date
      if (loadDate) return this.$moment(String(loadDate).substring(0, 10), 'YYYY-MM-DD')
      return this.$moment().startOf('day')
    },
    prepareParcelsFromOrder() {
      const { times, offset, daysBase } = this.order_config
      const base_date = this.baseDate()
      this.pix_cobranca_aux = []
      for (let parcel_index = 0; parcel_index < times; parcel_index++) {
        const days_add = parcel_index * daysBase + offset
        this.pix_cobranca_aux.push({
          value: '0.00',
          menu: false,
          due: base_date.clone().add(days_add, 'day').format('YYYY-MM-DD'),
        })
      }
    },
    handleChangeVrtSelection(data) {
      this.selected_vrts_data = { ...data }
    },
    loadClients() {
      if (this.is_from_order) return
      this.loading_clients = true
      Api.Client.index({ short: true })
        .then((response) => response.data)
        .then((body) => {
          const clients = body.data || body
          this.allClients = clients.filter((client) => {
            if (!this.form.place_id) return true
            const modalities = client.attributes?.modalities || client.modalities || []
            return !modalities.length || modalities.includes(this.form.place_id)
          })
        })
        .catch(() => {
          this.allClients = []
        })
        .finally(() => {
          this.loading_clients = false
        })
    },
    normalizeValue(value) {
      return String(this.parseMoney(value))
    },
    submitPixCobrancas() {
      if (!this.$refs.new_pix_cobranca_form.validate() || !this.can_submit) return
      this.saving = true
      const pix_cobranca_aux = this.is_from_order
        ? this.pix_cobranca_aux.map((parcel) => ({
          value: this.normalizeValue(parcel.value),
          due_date: parcel.due,
        }))
        : [{ value: this.paying_vrts_total.toFixed(2), due_date: this.standalone_due }]
      const payload = {
        company_key: this.company_key,
        pix_origin: 'cobranca',
        pix_cobranca_aux,
      }
      if (this.is_from_order) {
        payload.client_id = this.order_data.client_id
        payload.place_id = this.order_data.modality_id || this.order_data.place_id
        if (this.order_data.external_order_id || this.order_data.solution === 'retail') {
          payload.external_order_id = this.order_data.external_order_id || this.order_data.id
        } else {
          payload.order_id = this.order_data.order_id || this.order_data.id
        }
      } else {
        payload.client_id = this.form.client_id
        payload.place_id = this.form.place_id
        payload.payment_attributes = {
          client_id: this.form.client_id,
          kind: 'pix',
          total_value: this.selected_vrts_data.selected_vrts_ammount,
          status_id: 6,
          place_id: this.form.place_id,
          vrt_payments_attributes: this.selected_vrts_data.selected_vrts.map((vrt) => ({
            vrt_transaction_id: vrt.attributes.id,
            paying_ammount: Number(vrt.attributes.ammount - vrt.attributes.ammount_payed).toFixed(2),
          })),
        }
      }
      Api.Pix.createPixCobranca(payload)
        .then(() => {
          this.addNotification?.({ type: 'success', msg: 'PIX Cobrança gerado com sucesso!' })
          this.$emit('doneNewPixCobranca')
          this.dialog = false
        })
        .catch((err) => {
          this.addNotification?.({
            type: 'error',
            msg: err.response?.data?.errors?.[0] || 'Erro ao gerar PIX Cobrança',
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
  },
  mounted() {
    this.getSicoobCredentials?.()
    if (this.is_from_order) {
      this.prepareParcelsFromOrder()
    } else {
      this.loadClients()
    }
  },
  watch: {
    dialog(nv) {
      if (nv === false) this.$emit('close')
    },
    'form.place_id'() {
      this.form.client_id = null
      this.allClients = []
      this.loadClients()
    },
    'form.client_id'() {
      this.selected_vrts_data = { paying_vrts: [], selected_vrts: [], selected_vrts_ammount: '0.00', total_debt: '0.00' }
    },
  },
}
</script>

<style scoped>
.charge-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.charge-dialog-header {
  padding: 20px 24px;
}
.charge-dialog-header--pix {
  background: linear-gradient(135deg, #00695c 0%, #00897b 55%, #26a69a 100%);
}
.charge-summary-card {
  background: #f7f9fb;
  border: 1px solid #e8eef3;
  border-radius: 12px;
  padding: 12px 14px;
  height: 100%;
}
.charge-parcel-card {
  border-radius: 12px !important;
  border-color: #e8eef3 !important;
}
.charge-sum-box {
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid #e8eef3;
  background: #fafbfc;
}
.charge-sum-box--ok {
  border-color: #a5d6a7;
  background: #f1f8f4;
}
.charge-sum-box--err {
  border-color: #ffcc80;
  background: #fff8f0;
}
.charge-empty-box {
  border: 1px dashed #cfd8dc;
  border-radius: 12px;
  background: #fafbfc;
}
</style>
